/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CreateAudiCalculationModuleSettingDocument,
    type CreateAudiCalculationModuleSettingMutation,
    type CreateAudiCalculationModuleSettingMutationVariables,
} from '../../../../../api/mutations/createAudiCalculationModuleSetting';
import { type AudiCalculationSettingInitialInput, ModuleType } from '../../../../../api/types';
import { useDealerContext } from '../../../../../components/contexts/DealerContextManager';
import DealershipPriceDisclaimerField, {
    type DealershipPriceDisclaimerFieldProps,
} from '../../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import { type CompanySpecs } from '../../../AddModulePage/getAvailableModules';
import MarketTypeGroup from '../../modules/implementations/shared/MarketTypeGroup';
import useModuleOptions from '../../modules/implementations/shared/useModuleOptions';
import usePriceDisclaimerToolTipByMarket from '../../usePriceDisclaimerToolTipByMarket';
import validator from './validators';

export type CompanySpecsForAudiCalculation = Pick<
    CompanySpecs,
    'modules' | 'paymentSettings' | 'liveChatSettings' | 'autoplaySettings'
>;

export type AddAudiCalculationSettingsFormProps = {
    moduleId: string;
    company: CompanySpecsForAudiCalculation;
    onComplete: () => void;
};

const initialValues: AudiCalculationSettingInitialInput = {
    displayName: '',
    identifier: '',
    bankModuleId: null,
    vehicleModuleIds: [],
    insuranceModuleId: null,
    priceDisclaimer: {
        defaultValue: { defaultValue: '', overrides: [] },
        overrides: [],
    },
    preownedPriceDisclaimer: {
        defaultValue: { defaultValue: '', overrides: [] },
        overrides: [],
    },
};

type AudiCalculationDealershipPriceDisclaimerFieldProps = Omit<DealershipPriceDisclaimerFieldProps, 'itemProps'>;

const AudiCalculationDealershipPriceDisclaimerField = (props: AudiCalculationDealershipPriceDisclaimerFieldProps) => {
    const {
        values: { market },
    } = useFormikContext<AudiCalculationSettingInitialInput>();

    const priceDisclaimerToolTip = usePriceDisclaimerToolTipByMarket(market);
    const priceDisclaimerItemProps = useMemo(() => ({ tooltip: priceDisclaimerToolTip }), [priceDisclaimerToolTip]);

    return <DealershipPriceDisclaimerField {...props} itemProps={priceDisclaimerItemProps} />;
};

const AddAudiCalculationSettingsForm = ({ moduleId, onComplete, company }: AddAudiCalculationSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'audiCalculationModuleDetails']);
    const { dealersFromApi } = useDealerContext();

    const options = useModuleOptions(company);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: AudiCalculationSettingInitialInput) => {
            message.loading({
                content: t('audiCalculationModuleDetails:settings.create.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<
                    CreateAudiCalculationModuleSettingMutation,
                    CreateAudiCalculationModuleSettingMutationVariables
                >({
                    mutation: CreateAudiCalculationModuleSettingDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('audiCalculationModuleDetails:settings.create.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const vehicleModuleOptions = useMemo(() => [...options.vehicles, ...options.finderVehicles], [options]);

    const validate = useValidator(validator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addAudiCalculationSettings"
                    layout="vertical"
                    name="addAudiCalculationSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('moduleDetails:fields.audiCalculationSettingId', { returnObjects: true })}
                        name="identifier"
                        required
                    />
                    <AudiCalculationDealershipPriceDisclaimerField
                        {...t('moduleDetails:fields.priceDisclaimer', { returnObjects: true })}
                        dealers={dealersFromApi}
                        name="priceDisclaimer"
                    />
                    <AudiCalculationDealershipPriceDisclaimerField
                        {...t('moduleDetails:fields.preownedPriceDisclaimer', { returnObjects: true })}
                        dealers={dealersFromApi}
                        name="preownedPriceDisclaimer"
                    />
                    <MarketTypeGroup
                        dealers={dealersFromApi}
                        moduleType={ModuleType.AudiCalculationModule}
                        name="market"
                        asFragment
                        required
                    />
                    <SelectField
                        {...t('moduleDetails:fields.bankModuleId', { returnObjects: true })}
                        name="bankModuleId"
                        options={options.financing}
                        required
                        showSearch
                    />
                    <SelectField
                        {...t('moduleDetails:fields.insuranceModuleId', { returnObjects: true })}
                        name="insuranceModuleId"
                        options={options.insurance}
                        allowClear
                        showSearch
                    />
                    <SelectField
                        {...t('moduleDetails:fields.vehicleModuleId', { returnObjects: true })}
                        mode="multiple"
                        name="vehicleModuleIds"
                        options={vehicleModuleOptions}
                        required
                        showSearch
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddAudiCalculationSettingsForm;
